/**
 * ClientStorage class provides methods to interact with the browser's localStorage.
 * It ensures that operations are only performed in a browser environment.
 */
export class ClientStorage {
  /**
   * Creates an instance of ClientStorage.
   * Initializes a flag to check if the code is running in a browser environment.
   */
  constructor() {
    this.isBrowser =
      typeof window !== "undefined" && typeof localStorage !== "undefined";
  }

  /**
   * Retrieves an item from localStorage.
   * Parses the stored JSON value and returns it.
   * @param {string} storageKey - The key of the item to retrieve.
   * @returns {any} The parsed value from localStorage, or `undefined` if the key does not exist.
   * @throws {Error} If the method is called in a non-browser environment.
   */
  getItem = (storageKey) => {
    if (this.isBrowser) {
      const value = localStorage.getItem(storageKey);
      return value ? JSON.parse(value) : undefined; // Handle null values
    } else {
      throw new Error("Unable to execute on server");
    }
  };

  /**
   * Stores an item in localStorage.
   * Converts the value to a JSON string before storing it.
   * @param {string} storageKey - The key under which the value will be stored.
   * @param {any} value - The value to store in localStorage.
   * @throws {Error} If the method is called in a non-browser environment.
   */
  setItem = (storageKey, value) => {
    if (this.isBrowser) {
      localStorage.setItem(storageKey, JSON.stringify(value));
    } else {
      throw new Error("Unable to execute on server");
    }
  };

  /**
   * Removes an item from localStorage.
   * @param {string} storageKey - The key of the item to remove.
   * @throws {Error} If the method is called in a non-browser environment.
   */
  removeItem = (storageKey) => {
    if (this.isBrowser) {
      localStorage.removeItem(storageKey);
    } else {
      throw new Error("Unable to execute on server");
    }
  };
}
